import BaseController from './base_controller'

export default class extends BaseController {
  static targets = [
    "form",
    "mvgIndex",
    "effPeriodCostRatio",
    "incomeRatio",
    "costRatio",
    "vesselIndex",
    "averageIndex",
    "periodOneEarnings",
    "opex",
    "marketIndicatorForm",
    "calculateButton",
    "vesselDetailsForm",
    "speedsConsumptionForm",
    "speedAnalysis",
    "admiraltyContent"
  ]

  static values = {
    imo: String
  }

  connect() {
    this.element.addEventListener("calculator-vessel:openVessel", (event) => this.openVessel(event))
    this.element.addEventListener("calculator-vessel:closeVessel", () => this.closeVessel())
    this.element.addEventListener("calculator-vessel:openAdmiralty", () => this.openAdmiralty())
    this.element.addEventListener("calculator-vessel:closeAdmiralty", () => this.closeAdmiralty())
    this.element.addEventListener("calculator-vessel:updateResults", (event) => this.updateResults(event))
  }

  openVessel(event) {
    if (!this.hasFormTarget) return
    
    this.formTarget.classList.remove('d-none')
    
    const isNewVessel = event?.detail?.isNewVessel || false
    if (isNewVessel) {
      // Hide vessel list when showing form for new vessel
      const calculatorElement = document.querySelector('#calculator-mobile[data-controller*="calculator"]') || document.querySelector('[data-controller*="calculator"]')
      if (calculatorElement) {
        calculatorElement.dispatchEvent(new CustomEvent("calculator:closeVessels", { bubbles: true }))
      }
    } else {
      if (this.hasVesselDetailsFormTarget) {
        this.vesselDetailsFormTarget.classList.remove('d-none')
      }
      if (this.hasSpeedsConsumptionFormTarget) {
        this.speedsConsumptionFormTarget.classList.remove('d-none')
      }
    }
    
    if (this.hasMarketIndicatorFormTarget) {
      this.marketIndicatorFormTarget.classList.remove('d-none')
    }
  }

  closeVessel() {
    if (!this.hasFormTarget) return

    this.formTarget.classList.add('d-none')
  }

  openAdmiralty() {
    if (!this.hasAdmiraltyContentTarget) return

    if (this.hasFormTarget) {
      this.formTarget.classList.add('d-none')
    }
    this.admiraltyContentTarget.classList.remove('d-none')

    // Use the form that contains this vessel card so we always target the correct .scatter-graphs
    // (works on mobile and desktop; global selectors like #calculator-forms-wrapper don't exist on mobile)
    const form = this.element.closest('form')
    const vesselForm = form && typeof window.jQuery !== 'undefined' ? window.jQuery(form) : null
    this.initializeAdmiraltyCharts(vesselForm)
  }

  closeAdmiralty() {
    if (!this.hasAdmiraltyContentTarget) return

    this.admiraltyContentTarget.classList.add('d-none')
  }

  initializeAdmiraltyCharts(element) {
    if (typeof initCalcCharts === 'function') {
      initCalcCharts(element)
    }
  }

  updateResults(event) {
    const {
      indicator,
      netPresentValue,
      effectiveIncomeRatio,
      effectiveCostRatio,
      effectiveIncomeRatioClass,
      effectiveCostRatioClass,
      firstPeriodDailyEarnings,
      firstPeriodDailyOpex,
      vesselIndex,
      averageIndex
    } = event.detail

    if (this.hasMvgIndexTarget) {
      if (netPresentValue !== null && netPresentValue !== undefined) {
        const formattedValue = this.formatNetPresentValue(netPresentValue)
        this.mvgIndexTarget.textContent = formattedValue
      } else if (indicator) {
        const indicatorText = this.formatIndicator(indicator)
        this.mvgIndexTarget.textContent = indicatorText
      }
    }

    if (effectiveIncomeRatio !== undefined && effectiveCostRatio !== undefined) {
      const incomeRatio = this.formatPercentage(effectiveIncomeRatio)
      const costRatio = this.formatPercentage(effectiveCostRatio)

      if (this.hasIncomeRatioTarget && this.hasCostRatioTarget) {
        this.incomeRatioTarget.textContent = incomeRatio
        this.costRatioTarget.textContent = costRatio

        this.incomeRatioTarget.className = `score-result ${effectiveIncomeRatioClass || ''}`.trim()
        this.costRatioTarget.className = `score-result ${effectiveCostRatioClass || ''}`.trim()
      }
    }

    if (this.hasPeriodOneEarningsTarget && this.hasOpexTarget &&
        firstPeriodDailyEarnings !== undefined && firstPeriodDailyOpex !== undefined) {
      const earnings = this.formatNumber(firstPeriodDailyEarnings)
      const opex = this.formatNumber(firstPeriodDailyOpex)
      this.periodOneEarningsTarget.textContent = `$ ${earnings}/day`
      this.opexTarget.textContent = `$ ${opex}/day`
    }

    if (this.hasVesselIndexTarget && vesselIndex !== undefined) {
      if (vesselIndex === null) {
        this.vesselIndexTarget.textContent = '-'
        this.vesselIndexTarget.className = ''
      } else {
        this.vesselIndexTarget.textContent = this.formatPercentage(vesselIndex)
        this.vesselIndexTarget.className = 'score-result color-blue'
      }
    }

    if (this.hasAverageIndexTarget && averageIndex !== undefined) {
      if (averageIndex === null) {
        this.averageIndexTarget.textContent = '-'
        this.averageIndexTarget.className = ''
      } else {
        this.averageIndexTarget.textContent = this.formatPercentage(averageIndex)
        this.averageIndexTarget.className = 'score-result color-blue'
      }
    }
  }

  formatIndicator(indicator) {
    if (typeof indicator === 'number') {
      return `${indicator.toFixed(1)}%`
    }
    return indicator ? indicator.charAt(0).toUpperCase() + indicator.slice(1) : '100%'
  }

  formatPercentage(value) {
    if (value === null || value === undefined) return 'N/A'
    if (value === 'Insufficient Data') return value
    return `${Number(value).toFixed(1)}%`
  }

  formatNumber(value) {
    if (value === null || value === undefined) return '100%'
    return Math.round(value).toLocaleString()
  }

  formatNetPresentValue(value) {
    if (value === null || value === undefined) return '-'
    // Round to millions (divide by 1,000,000 and round to 2 decimal places)
    const millions = value / 1000000
    // Remove trailing zeros from decimal part
    const formatted = millions.toFixed(1).replace(/\.?0+$/, '')
    return `${formatted} mUSD`
  }

  setCalculateDisabled(event) {
    const { missing } = event.detail
    this.calculateButtonTargets?.forEach(btn => {
      btn.disabled = missing
      btn.classList.toggle('disabled', missing)
    })
  }
}
